import { Content, Header, SubNav } from '../../libs/gs-component-right-rail';
import { Field } from '@xo-union/tk-component-fields';
import { ToggleSwitch } from '@xo-union/tk-component-switches';
import { Body1 } from '@xo-union/tk-ui-typography';
import { DisplayButton } from '@xo-union/tk-ui-links';
import Honeybadger from 'honeybadger-js';
import produce from 'immer';
import React, { RefObject, useCallback, useContext, useEffect, useState } from 'react';
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd';
import TransientRoomRailCard from '../TansientRoomRailCard';
import { Items } from '../Card';

import { TrackingContext } from '../../TrackingContext';
import {
  PagesQuery,
  UpdatePageMutation,
  usePageQuery,
  useReorderPageItemsMutation,
  useUpdatePageMutation,
  useWeddingWebsiteQuery,
} from '../../generated/graphql';
import { getTargetOrigin } from '../../helpers/getTargetOrigin';
import {
  LITE_SITE_PAGE_TYPE,
  REGISTRY_PAGE_TYPE,
  TRAVEL_PAGE_TYPE,
  WEDDING_PAGE_TYPE,
  CUSTOM_PAGE_TYPE,
} from '../../helpers/pages';
import { reorder } from '../../helpers/reorder';
import Item from '../Item';
import { pagesQuery } from '../LeftContainer/graphql';
import CoverPhotoPortrait from '../LiteHomePageCard/CoverPhotoPortrait';
import OnlineRsvp from '../LiteHomePageCard/OnlineRsvp';
import AddToPageButton from '../PageCard/AddToPageButton';
import shared from '../PageSettingsRail/main.scss';
import { RegistryPageCard } from '../RegistryPageCard';
import styles from './editPageRail.scss';
import CoverPhoto from '../WeddingPageCard/CoverPhoto';
import BasicInfoItem from '../WeddingPageCard/BasicInfoItem';
import useDatePref from '../../helpers/useDatePref';
import { basicInfoRailRoute, previewRoute } from '../../helpers/routes';
import { useHistory, useLocation } from 'react-router-dom';
import Icon from '@xo-union/tk-component-icons';
import { MarketPlaceCallout } from '../CalloutCard/MarketPlaceCallout';
import { CollapsibleRailSection } from './CollapsibleRailSection';
import { useWebsiteLayoutContext } from '../../context/WebsiteLayoutContext';

type Props = {
  pageId?: number;
  setEditPageOpen: (v: boolean) => void;
  leftButtonOnClick?: () => void;
  iframeRef: RefObject<HTMLIFrameElement>;
};

const EditPageRail: React.FC<Props> = ({ pageId, setEditPageOpen, leftButtonOnClick, iframeRef }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { data: pageData } = usePageQuery({
    variables: {
      id: pageId as number,
    },
    skip: !pageId,
  });
  const { currentLayout } = useWebsiteLayoutContext();
  const { data } = useWeddingWebsiteQuery();
  const [updatePage] = useUpdatePageMutation();
  const { track } = useContext(TrackingContext);
  const [title, setTitle] = useState(pageData?.page.title || '');
  const [refetchBookings, setRefetchBookings] = useState(false);

  const page = pageData?.page;
  const isHome = page?.type === WEDDING_PAGE_TYPE;
  const [sortedItems, setSortedItems] = useState(page?.items || []);
  const [reorderPageItems] = useReorderPageItemsMutation();
  const [weddingDateText] = useDatePref({
    isLiteSite: false,
  });

  const subNavNames = ['Content', 'Settings'];
  const SUBNAV_CONTENT_INDEX = subNavNames.indexOf('Content');
  const [activeTabIndex, setActiveTabIndex] = useState(SUBNAV_CONTENT_INDEX);

  const subNav = <SubNav subNavNames={subNavNames} activeTabIndex={activeTabIndex} onTabClick={setActiveTabIndex} />;

  useEffect(() => {
    setSortedItems(page?.items || []);
  }, [page]);

  const openBasicInfoEdit = () => {
    history.push(basicInfoRailRoute(pathname.includes('preview')));
  };

  const onDragEnd = async ({ source, destination }: DropResult) => {
    if (!page) return;

    const sorted = reorder(sortedItems, source.index, destination?.index);
    setSortedItems(sorted);

    const updated = sorted.map(({ id, rank }: any) => ({
      id,
      rank,
    }));

    try {
      await reorderPageItems({
        variables: {
          attributes: {
            pageId: page.id,
            pageItems: updated,
          },
        },
        update(cache, { data }) {
          const { pages } = cache.readQuery<PagesQuery>({ query: pagesQuery })!;

          const nextState = produce(pages, draft => {
            const pg = draft.find(i => i.id === page.id);

            if (pg) {
              pg.items = data?.reorderPageItems!;
            }
          });

          track('WWS Page Edited', {
            pageType: page.type,
            action: 'edit',
            actionName: 'reorder items',
            pageVisible: page.show,
          });
          cache.writeQuery({ query: pagesQuery, data: { pages: nextState } });
        },
      });
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error reordering page cards',
        context: {
          error,
        },
      });
      setSortedItems(page?.items || []);
    }
  };

  const postMessage = useCallback(
    (updatedData?: UpdatePageMutation['updatePage'] | string | null, type: 'Page' | 'Registry' = 'Page') => {
      if (iframeRef && iframeRef.current && iframeRef.current.contentWindow && updatedData) {
        iframeRef.current?.contentWindow.postMessage(
          JSON.stringify({
            type: type === 'Page' ? 'updatePage' : 'updateRegistryNote',
            data: updatedData,
          }),
          getTargetOrigin()
        );
      }
    },
    [iframeRef]
  );

  useEffect(() => {
    setTitle(pageData?.page.title || '');
  }, [pageData]);

  useEffect(() => {
    // Send update on load so if you add something it doesn't get missed
    postMessage(pageData?.page);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!page) {
    return null;
  }

  const handleBlur = async () => {
    try {
      if (page.id) {
        const { data } = await updatePage({
          variables: {
            id: page.id,
            attributes: { title },
          },
        });
        track('WWS Page Edited', {
          pageType: data?.updatePage?.type,
          action: 'edit',
          actionName: 'visibility',
          pageVisible: data?.updatePage?.show,
          source: 'preview edit',
        });

        // update location param for custom pages
        if (data?.updatePage?.type === CUSTOM_PAGE_TYPE) {
          history.push({
            pathname: previewRoute,
            search: `?location=${data?.updatePage?.routeName}`,
          });
        }

        postMessage(data?.updatePage);
      }
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error updating page',
        context: {
          error,
        },
      });
    }
  };

  const toggleVisibility = async () => {
    try {
      const { data } = await updatePage({
        variables: {
          id: page.id as number,
          attributes: { show: !page.show },
        },
      });
      track('WWS Page Edited', {
        pageType: data?.updatePage?.type,
        action: 'edit',
        actionName: 'visibility',
        pageVisible: data?.updatePage?.show,
        source: 'preview edit',
      });
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({
          type: 'reload',
        }),
        getTargetOrigin()
      );
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error updating page',
        context: {
          error,
        },
      });
    }
  };

  const basicInfoHeader = (
    <Body1 bold className={styles.basicInfoTitle}>
      Basic Info
      <DisplayButton
        className={styles.basicInfoEditButton}
        type="button"
        tabIndex={0}
        onClick={openBasicInfoEdit}
        data-testid="basic-info-edit"
      >
        <Icon name="edit" size="sm" className={styles.basicInfoIcon} />
      </DisplayButton>
    </Body1>
  );

  const itemTitleLookup = (itemType: string) => {
    const name = itemTitle[itemType];
    return <Body1 bold> {name}</Body1>;
  };

  const itemTitle: { [key: string]: string } = {
    AccommodationItem: 'Accommodations',
    ActivityItem: 'Activities',
    HeadlineItem: 'Headlines',
    ParagraphItem: 'Paragraphs',
    PersonItem: 'People',
    StoryItem: 'Stories',
    BasicItem: 'Stories',
    TransportationItem: 'Transportation',
    PhotoItem: 'Photos',
    PhotoGalleryItem: 'Photo Galleries',
    PhotoTimelineItem: 'Photo Timelines',
    EventGroupItem: 'Events',
    GifItem: 'GIFs',
    EmbedItem: 'Embeds',
    QuestionItem: 'Questions',
    LivestreamItem: 'Livestreams',
    DressCodeItem: 'Dress Code',
  };

  const isSinglePageSite = currentLayout === 'single-page';
  const pageOrSection = isSinglePageSite ? 'Section' : 'Page';

  return (
    <>
      <Header
        onClose={() => setEditPageOpen(false)}
        leftButtonOnClick={leftButtonOnClick}
        leftButtonText={leftButtonOnClick !== undefined ? 'Back' : ''}
        subNav={subNav}
      >
        <div>{page.type === WEDDING_PAGE_TYPE ? 'Home' : page.title}</div>
      </Header>
      <Content className={styles.editRailContainer}>
        {activeTabIndex !== SUBNAV_CONTENT_INDEX ? (
          <>
            {page.type !== LITE_SITE_PAGE_TYPE && (
              <div className={`${shared.toggle} ${isHome ? styles.disabledToggle : ''}`}>
                <>
                  <Body1 className={styles.bold}>{`${pageOrSection} details`}</Body1>
                  <ToggleSwitch
                    data-testid={`${page.title}-visible`}
                    checked={page.show}
                    labelDirection="left"
                    onChange={toggleVisibility}
                    disabled={isHome}
                  >
                    {page.show ? 'Visible' : 'Hidden'}
                  </ToggleSwitch>
                </>
              </div>
            )}
            <Field
              name="title"
              onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => setTitle(target.value)}
              value={isHome ? 'Home' : title}
              onBlur={handleBlur}
              disabled={isHome}
            />
          </>
        ) : (
          <>
            {page.type === REGISTRY_PAGE_TYPE ? (
              <RegistryPageCard page={page} />
            ) : page.type === LITE_SITE_PAGE_TYPE ? (
              <>
                <div className={styles.infoContainer} data-testid="lite-site-home-content">
                  <CoverPhotoPortrait page={page} iframeRef={iframeRef} />
                </div>
                <OnlineRsvp />
              </>
            ) : (
              <>
                {page.type === WEDDING_PAGE_TYPE && (
                  <>
                    <CoverPhoto pageId={page.id} iframeRef={iframeRef} />
                    <CollapsibleRailSection sectionTitle={basicInfoHeader}>
                      <BasicInfoItem data={data} weddingDateText={weddingDateText} rightRail isPreview />
                    </CollapsibleRailSection>
                  </>
                )}
                {activeTabIndex === SUBNAV_CONTENT_INDEX && (
                  <>
                    <div data-testid={`${page.title}-content`}>
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId={`${page.id}-${page.title}-droppable-1`} type={`PAGE_ITEM_${page.type}`}>
                          {provided => (
                            <Items innerRef={provided.innerRef} {...provided.droppableProps}>
                              {sortedItems.map((item, i) => {
                                if (isHome && item.type === 'EventGroupItem') {
                                  return (
                                    <CollapsibleRailSection sectionTitle={itemTitleLookup(item.type)} isLast={true}>
                                      <Item
                                        key={`${item.type}-${item.id}`}
                                        item={item}
                                        page={page}
                                        index={i}
                                        preview
                                        iframeRef={iframeRef}
                                        hideTitle
                                        // toggle refetch instead of explicitly setting to true
                                        onUpdate={() => setRefetchBookings(!refetchBookings)}
                                      />
                                    </CollapsibleRailSection>
                                  );
                                }
                                return (
                                  <Item
                                    key={`${item.type}-${item.id}`}
                                    item={item}
                                    page={page}
                                    index={i}
                                    preview
                                    iframeRef={iframeRef}
                                  />
                                );
                              })}
                              {provided.placeholder}
                            </Items>
                          )}
                        </Droppable>
                      </DragDropContext>
                      {page.type === TRAVEL_PAGE_TYPE && <TransientRoomRailCard page={page} />}
                      <div className={styles.addToPage}>
                        <AddToPageButton page={page} preview />
                      </div>
                    </div>
                  </>
                )}
                {isHome ? <MarketPlaceCallout refetch={refetchBookings} /> : null}
              </>
            )}
          </>
        )}
      </Content>
    </>
  );
};

export default EditPageRail;
